<template>
  <validation-observer ref="observer" tag="div" class="add-edit-machine">
    <oie-form-wrapper class="add-edit-machine__form" @submit="handleSubmit">
      <div class="input-column__container add-edit-machine__section--images">
        <h2 v-tooltip.top="tStatic('selectProfileImageTooltip')" class="add-edit-machine-form__sub-title indicate-tooltip">
          {{ tStatic('selectProfileImage') }}
        </h2>
        <div>
          <oie-base-file-uploader
            v-model="profileImage"
            accept="image/*"
            icon="image-icon"
            :drag-text="tStatic('dragImageFileText')"
            :click-to-upload-text="tStatic('clickToUploadImageText')"></oie-base-file-uploader>
          <oie-file-list
            :files="currentProfileImage"
            key-val="fileName"
            class="add-edit-machine__file-list"
            @delete="markForDeletion('currentProfileImage', $event)"></oie-file-list>
        </div>
        <h2 v-tooltip.top="tStatic('selectOtherImagesTooltip')" class="add-edit-machine-form__sub-title indicate-tooltip">
          {{ tStatic('selectOtherImages') }}
        </h2>
        <div>
          <oie-base-file-uploader
            v-model="otherImages"
            multiple="true"
            accept="image/*"
            icon="image-icon"
            :drag-text="tStatic('dragImageFileText')"
            :click-to-upload-text="tStatic('clickToUploadImageText')"></oie-base-file-uploader>
          <oie-file-list
            :files="currentOtherImages"
            key-val="fileName"
            class="add-edit-machine__file-list"
            @delete="markForDeletion('currentOtherImages', $event)"></oie-file-list>
        </div>
      </div>

      <div class="input-column__container">
        <h2 class="add-edit-machine-form__sub-title">
          {{ tStatic('basicMachineInfo') }}
        </h2>
        <validation-provider v-slot="{ errors }" rules="required" slim>
          <oie-base-input
            id="machine-name"
            v-model="machine.name"
            :label-text="`${tStatic('machineName')}*`"
            :error-messages="errors"></oie-base-input>
        </validation-provider>

        <oie-base-dropdown
          v-model="machine.machineOwnerId"
          class="add-edit-machine-form__owner"
          :enable-deselection="true"
          :use-full-width="true"
          :initial-text="tStatic('selectMachineOwner')"
          :label-text="tStatic('newMachineMachineOwner')"
          :options="machineOwnersOptions"></oie-base-dropdown>

        <h2 class="add-edit-machine-form__sub-title">
          {{ tStatic('selectVehicle') }}
        </h2>

        <oie-base-vehicle-selector
          v-if="vehicles"
          :vehicles="vehicles"
          :pre-selected-vehicle="preSelectedVehicle"
          :selected-type.sync="machine.type"
          :selected-brand.sync="machine.manufacturer"
          :selected-model.sync="machine.model"></oie-base-vehicle-selector>

        <oie-base-input
          id="machine-info"
          v-model="machine.info"
          :label-text="`${tStatic('otherInfo')}`"
          :tooltip="`${tStatic('otherInfoTooltip')}`"
          :textarea="true"
          :max-length="800"></oie-base-input>

        <div class="add-edit-machine-form__section">
          <button
            v-tooltip.top="tStatic('advancedFieldsTooltip')"
            type="button"
            class="add-edit-machine-form__section-toggler"
            @click="showAdvancedFields = !showAdvancedFields">
            <h2 class="add-edit-machine-form__sub-title">
              {{ tStatic('advancedFields') }}
            </h2>
            <oie-base-icon icon="chevron-down"></oie-base-icon>
          </button>

          <div v-if="showAdvancedFields" class="add-edit-machine-form__section-content">
            <!-- SECTION ADVANCED FIELDS -->

            <oie-base-input
              id="machine-serial-number"
              v-model="machine.serialNumber"
              :label-text="`${tStatic('machineSerialNumber')}`"></oie-base-input>

            <oie-base-input
              id="machine-year-model"
              v-model="machine.yearModel"
              :label-text="`${tStatic('machineYearModel')}`"></oie-base-input>

            <oie-base-input
              id="machine-motor-type"
              v-model="machine.motorType"
              :label-text="`${tStatic('machineMotorType')}`"></oie-base-input>

            <oie-base-input
              id="machine-motor-number"
              v-model="machine.motorNumber"
              :label-text="`${tStatic('machineMotorNumber')}`"></oie-base-input>

            <oie-base-input
              id="machine-cabin-number"
              v-model="machine.cabinNumber"
              :label-text="`${tStatic('machineCabinNumber')}`"></oie-base-input>

            <oie-base-input
              id="machine-front-axle-number"
              v-model="machine.frontAxleNumber"
              :label-text="`${tStatic('machineFrontAxleNumber')}`"></oie-base-input>

            <oie-base-input
              id="machine-rear-axle-number"
              v-model="machine.rearAxleNumber"
              :label-text="`${tStatic('machineRearAxleNumber')}`"></oie-base-input>

            <oie-base-input
              id="machine-gearbox-type"
              v-model="machine.gearboxType"
              :label-text="`${tStatic('machineGearboxType')}`"></oie-base-input>

            <!-- SECTION CUSTOM FIELDS -->

            <h2 class="add-edit-machine-form__sub-title">
              {{ tStatic('customFieldsTitle') }}
            </h2>

            <div v-for="(val, key) in machine.customFields" :key="`custom-field-${key}input`" class="add-edit-machine-form__custom-field-container">
              <oie-base-input
                :id="`custom-field-${key}input`"
                v-model="machine.customFields[key]"
                :label-text="`${metaDataMachineFields[key]}`"></oie-base-input>
              <oie-base-action v-tooltip.top="tStatic('removeNewFieldTooltip')" theme="destructive" @click="removeCustomField(key)">
                <oie-base-icon icon="trash-can" size="xs"></oie-base-icon>
              </oie-base-action>
            </div>

            <oie-base-dropdown
              v-if="showCustomFieldInput"
              :label-text="tStatic('newFieldName')"
              :options="unusedCustomFields"
              :initial-text="tStatic('selectCustomField')"
              :tooltip="tStatic('selectCustomFieldTooltip')"
              :enable-add-option="true"
              @selection="handleSelectCustomField"
              @add-option="showAddCustomFieldModal"></oie-base-dropdown>

            <oie-base-action v-tooltip.top="tStatic('addNewFieldTooltip')" theme="outlined" @click="showCustomFieldInput = !showCustomFieldInput">
              {{ tStatic('addNewField') }}
            </oie-base-action>
          </div>
        </div>
      </div>
      <oie-action-wrapper class="add-edit-machine__actions">
        <template #left>
          <oie-base-action
            v-if="withDelete"
            size="large"
            theme="destructive"
            @click="$emit('delete')">
            <template #iconPrepend>
              <oie-base-icon icon="trash-can" size="xs"></oie-base-icon>
            </template>
            {{ tStatic('deleteMachine') }}
          </oie-base-action>
        </template>
        <template #right>
          <oie-base-action size="large" theme="skeleton" @click="handleCancel">
            {{ tStatic('cancel') }}
          </oie-base-action>
          <oie-base-action :class="{'element--is-loading element--is-loading--small': isSaving}" size="large" type="submit">
            {{ tStatic('saveMachineOrOwner') }}
          </oie-base-action>
        </template>
      </oie-action-wrapper>
    </oie-form-wrapper>
  </validation-observer>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Vue from 'vue'
import httpService from '@/core/http-service'
import imageCompressor from '@/core/image-service'
import _ from 'lodash'
import utils from '@/core/utils'

export default {
  props: {
    withDelete: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSaving: false,
      showAdvancedFields: false,
      showCustomFieldInput: false,
      profileImage: [],
      otherImages: [],
      imagesMarkedForDeletion: [],
      machine: {
        cabinNumber: '',
        createdBy: '',
        customerNumber: '',
        customFields: {},
        frontAxleNumber: '',
        gearboxType: '',
        images: [],
        info: '',
        machineId: '',
        machineOwnerId: null,
        manufacturer: '',
        model: '',
        motorNumber: '',
        motorType: '',
        name: '',
        rearAxleNumber: '',
        serialNumber: '',
        type: '',
        yearModel: ''
      }
    }
  },
  computed: {
    ...mapGetters('myMachines', ['machineOwnersOptions', 'vehicles', 'currentMachine', 'hasCurrentMachine', 'metaDataMachineFields', 'machineCustomFieldOptions']),
    hasCustomFields() { return !_.isEmpty(this.machine.customFields) },
    preSelectedVehicle() {
      if(this.hasCurrentMachine) {
        return { type: this.currentMachine.type, brand: this.currentMachine.manufacturer, model: this.currentMachine.model }
      }
      return null
    },
    unusedCustomFields() {
      return _.filter(this.machineCustomFieldOptions, item => !_.has(this.machine.customFields, item.value))
    },
    hasProfileImagesUpload() { return this.profileImage.length > 0 },
    hasOtherImagesUpload() { return this.otherImages.length > 0 },
    hasImagesMarkedForDeletion() { return this.imagesMarkedForDeletion.length > 0 },
    currentProfileImage() {
      return _.filter(this.machine.images, img => img.type === 'profile' && !_.includes(this.imagesMarkedForDeletion, img.imageId))
    },
    currentOtherImages() {
      return _.filter(this.machine.images, img => img.type !== 'profile' && !_.includes(this.imagesMarkedForDeletion, img.imageId))
    }
  },
  watch: {
    profileImage() {
      if(this.currentProfileImage.length > 0) {
        this.imagesMarkedForDeletion.push(this.currentProfileImage[0].imageId)
      }
    }
  },
  beforeMount() {
    if(this.hasCurrentMachine) {
      this.machine = _.merge(this.machine, this.currentMachine)
    }
  },
  methods: {
    ...mapActions('myMachines', ['addMachine', 'setMyMachinesMetaData', 'addMachineImages', 'deleteMachineImages']),
    async handleSubmit() {
      const isValid = await this.$refs.observer.validate()
      if(isValid) {
        try {
          this.isSaving = true
          let addedOrEditedMachine = await this.addMachine(this.machine)
          if(this.hasProfileImagesUpload) {
            addedOrEditedMachine = await this.uploadImage(addedOrEditedMachine.machineId, this.profileImage, 'profile')
          }
          if(this.hasOtherImagesUpload) {
            addedOrEditedMachine = await this.uploadImage(addedOrEditedMachine.machineId, this.otherImages, null)
          }
          if(this.hasImagesMarkedForDeletion) {
            addedOrEditedMachine = await this.deleteImages()
          }
          this.$emit('save', addedOrEditedMachine)
        } finally {
          this.isSaving = false
        }
      } else {
        const errors = document.querySelectorAll('.add-edit-machine__form .input__message--error')
        if (errors && errors.length > 0 && !utils.isElementInView(errors[0])) {
          errors[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    },
    async uploadImage(machineId, images, type) {
      const compressedImages = await imageCompressor.compress(images)
      const numImages = compressedImages.length
      for (let i = 0; i < numImages; i++) {
        const image = compressedImages[i]
        const result = await this.addMachineImages({ machineId: machineId, image: image, type: type })
        const isLastIteration = i === numImages - 1

        if(isLastIteration) {
          return result.data
        }
      }
    },
    async deleteImages() {
      const numImages = this.imagesMarkedForDeletion.length
      for (let i = 0; i < numImages; i++) {
        const imageId = this.imagesMarkedForDeletion[i]
        const result = await this.deleteMachineImages({ imageId: imageId })
        const isLastIteration = i === numImages - 1

        if(isLastIteration) {
          return result.data
        }
      }
    },
    handleCancel() {
      this.$emit('cancel')
    },
    async showAddCustomFieldModal($event) {
      this.publish('modal.show.add-option', { header: this.tStatic('addNewOptionHeader'),
        text: this.tStatic('addNewOptionDescription'),
        initialValue: $event,
        action:
          {
            text: this.tStatic('addNewOptionText'),
            handler: this.handleAddCustomField
          }
      })
    },
    async handleAddCustomField(fieldType) {
      if (fieldType) {
        const response = (await httpService.put('my-machines/meta-data/machine-field-type', { fieldType })).data
        await this.setMyMachinesMetaData(response)
        const machineFieldId = _.findKey(response.machineFields, field => field === fieldType)
        if (machineFieldId) Vue.set(this.machine.customFields, machineFieldId, '')
      }
    },
    removeCustomField(key) {
      Vue.delete(this.machine.customFields, key)
    },
    handleSelectCustomField(selection) {
      if(selection) {
        Vue.set(this.machine.customFields, selection.value, '')
      }
    },
    markForDeletion(imageList, index) {
      const image = this[imageList][index]
      this.imagesMarkedForDeletion.push(image.imageId)
    }
  }
}
</script>

<style lang="scss">
.add-edit-machine {
  container-type: inline-size;
  position: relative;
}

.add-edit-machine__form {
  display: grid;
  grid-template-columns: 1fr;
  gap: measure(half);

  @container (min-width: 900px) {
    grid-template-columns: 1fr 2fr;
    column-gap: measure(full);
  }
}

.add-edit-machine-form__section {
  border-top: 1px solid color(border-gray);
  border-bottom: 1px solid color(border-gray);
}

.add-edit-machine-form__section-toggler {
  @include reset-button;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: measure(half) 0;
}

.add-edit-machine-form__section-content >* {
  margin-bottom: measure(full);
}

.add-edit-machine__actions {
  grid-column: 1 / -1;
  order: 2;
}

.add-edit-machine__file-list {
  margin-top: measure(eighth);
}

.add-edit-machine__section--images {
  order: 1;

  @container (min-width: 900px) {
    order: 0;
  }
}

.add-edit-machine-form__sub-title {
  @include h3-semi-bold;
  display: inline-block;
}

.add-edit-machine-form__owner .dropdown__options {
  z-index: 4;
}

.add-edit-machine-form__custom-field-container {
  display: flex;
  align-items: center;

  button {
    position: relative;
    top: 14px;
    margin-left: 10px;
  }
}

</style>
